import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { getLocationDetails } from '../services/api';
import { locationDetailsCache, locationDetailsInFlight } from '../services/locationDetailsCache';
import { getLogoUrlFromMedia } from '../constants';
import { pickTimeZoneCdFromLocationPayload } from '../utils/locationPayload';
import Icon from './Icon';
import { C } from './theme';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    const tokenKeys = ['accessToken', 'access_token', 'token'];
    for (const key of tokenKeys) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const SUPER_ROLES = ['Restaurant_Owner', 'Regional_Employee', 'Magil_Admin'];

const OutletDropdown = () => {
    const location = useLocation();
    const { auth } = useAuth();
    const { selectedBranch: branch, setSelectedBranch } = useBranch();
    const [open, setOpen] = useState(false);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const ref = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [dropdownRect, setDropdownRect] = useState<{ top: number; left: number; width: number } | null>(null);
    const lastLocationDetailsPayload = useRef<unknown>(null);

    const token = findToken(auth);
    const locationId = branch?.id ?? branch?.locationId ?? auth?.locationId ?? auth?.location_id;
    const userRole = auth?.role ?? auth?.designation ?? '';
    const isSuperUser = SUPER_ROLES.includes(userRole);
    const disabledPaths = ['/employees/add', '/employees/edit/'];
    const isDropdownDisabledByRoute = disabledPaths.some((p) => location.pathname.includes(p));
    const canSwitchOutlets = isSuperUser && !isDropdownDisabledByRoute;

    const fullBranchName = branch?.locationName || branch?.name || 'Select outlet';
    const branchName = fullBranchName.includes(',') ? fullBranchName.split(',').pop().trim() : fullBranchName;

    // Apply GET /details payload (logo / timezone / working hours) to the selected branch.
    // Mirrors DesktopLayout.applyLocationDetailsToBranch so report screens (which render
    // outside DesktopLayout) get the same branch enrichment when the outlet changes.
    const applyLocationDetailsToBranch = (payload: any, currentId: any) => {
        if (!payload) return;
        const list = payload?.branch ?? payload?.branches ?? [];
        const hasList = Array.isArray(list) && list.length > 0;
        const match = hasList ? list.find((loc: any) => (loc.id ?? loc.locationId) === currentId) : null;
        // API may return single-location details with media at top level (like restaurantDetails)
        const singleDetails = !hasList && payload?.media && Array.isArray(payload.media) ? payload : null;
        const source = match ?? singleDetails;
        if (!source) return;
        const logoUrl = getLogoUrlFromMedia(source.media) || source.logoUrl || '';
        const locationName = source.locationName ?? source.name ?? source.branchName ?? branch?.locationName;
        const timeZoneCd =
            pickTimeZoneCdFromLocationPayload(payload) ?? pickTimeZoneCdFromLocationPayload(source);
        setSelectedBranch({
            ...branch,
            ...source,
            id: source.id ?? source.locationId ?? currentId,
            locationName,
            logoUrl: logoUrl || branch?.logoUrl,
            ...(timeZoneCd ? { timeZoneCd } : {}),
            workingHours: payload?.workingHours || source?.workingHours || branch?.workingHours || [],
            // `source` (matched item from payload.branch/branches) is not a reliable
            // source of country — some environments omit it from every branch[] entry.
            // Only the top-level payload.country is consistently correct, so use ONLY
            // that (no fallback to source/branch) — every report screen's currency
            // formatting reads `branch?.country`, so this must resolve to the top-level
            // API value, never a stale or nested one.
            country: payload?.country,
        });
    };

    // Fetch GET /details whenever the selected outlet changes. Report screens mount this
    // dropdown outside DesktopLayout, so without this the details API is never called on
    // outlet switch. Uses the shared cache so it dedupes with DesktopLayout's fetch.
    useEffect(() => {
        if (!locationId || !token) return;
        let cancelled = false;
        const currentId = branch?.id ?? branch?.locationId ?? locationId;
        const cacheKey = String(locationId);

        const cachedPayload = locationDetailsCache.get(cacheKey);
        if (cachedPayload) {
            lastLocationDetailsPayload.current = cachedPayload;
            applyLocationDetailsToBranch(cachedPayload, currentId);
            return () => { cancelled = true; };
        }

        const inFlight = locationDetailsInFlight.get(cacheKey);
        if (inFlight) {
            inFlight
                .then((payload) => {
                    if (cancelled || !payload) return;
                    lastLocationDetailsPayload.current = payload;
                    applyLocationDetailsToBranch(payload, currentId);
                })
                .catch(() => {});
            return () => { cancelled = true; };
        }

        const req = getLocationDetails(locationId, token)
            .then((res) => {
                const payload = res?.metaDataInfo?.data ?? res?.data ?? res;
                if (payload) locationDetailsCache.set(cacheKey, payload);
                return payload;
            })
            .finally(() => { locationDetailsInFlight.delete(cacheKey); });
        locationDetailsInFlight.set(cacheKey, req);

        req
            .then((payload) => {
                if (cancelled || !payload) return;
                lastLocationDetailsPayload.current = payload;
                applyLocationDetailsToBranch(payload, currentId);
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [locationId, token]);

    useEffect(() => {
        if (!open || !locationId || !token) {
            if (!open) return;
            setLocations([]);
            setError(!locationId ? 'No location' : null);
            return;
        }
        setError(null);
        setLoading(true);
        let cancelled = false;
        getLocationDetails(locationId, token)
            .then((res) => {
                if (cancelled) return;
                const payload = res?.metaDataInfo?.data ?? res?.data ?? res;
                lastLocationDetailsPayload.current = payload;
                const list = payload?.branch ?? payload?.branches ?? [];
                setLocations(Array.isArray(list) ? list : []);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err?.message || 'Failed to load branches');
                setLocations([]);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, locationId, token]);

    useLayoutEffect(() => {
        if (open && triggerRef.current) {
            const r = triggerRef.current.getBoundingClientRect();
            setDropdownRect({ top: r.bottom + 4, left: r.left, width: r.width });
        }
    }, [open]);

    useEffect(() => {
        const onOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) {
            document.addEventListener('click', onOutside);
            return () => document.removeEventListener('click', onOutside);
        }
    }, [open]);

    const selectBranch = (loc: any) => {
        const id = loc.id ?? loc.locationId;
        const name = loc.locationName ?? loc.name ?? 'Branch';
        const logoUrl = getLogoUrlFromMedia(loc.media) || loc.logoUrl || '';
        const timeZoneCd =
            pickTimeZoneCdFromLocationPayload(lastLocationDetailsPayload.current) ??
            pickTimeZoneCdFromLocationPayload(loc);
        const payload = lastLocationDetailsPayload.current as any;
        setSelectedBranch({
            ...loc,
            id: loc.id ?? id,
            locationName: loc.locationName ?? loc.name ?? name,
            logoUrl,
            ...(timeZoneCd ? { timeZoneCd } : {}),
            workingHours: payload?.workingHours || loc?.workingHours || [],
        });
        setOpen(false);
    };

    // Non-super users cannot switch outlets — show read-only label
    if (isDropdownDisabledByRoute) {
        return (
            <div className="mobile-outlet-readonly-label">{branchName}</div>
        );
    }

    if (!canSwitchOutlets) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px' }}>
                <Icon name="location_on" size={14} color={C.accent} />
                <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{branchName}</span>
            </div>
        );
    }

    return (
        <div ref={ref}>
            <div
                ref={triggerRef}
                role="button"
                tabIndex={0}
                onClick={() => setOpen((o) => !o)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setOpen((o) => !o); }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '5px 10px',
                    borderRadius: 8,
                    background: C.surface,
                    border: `1px solid ${C.divider}`,
                    cursor: 'pointer',
                }}
            >
                <Icon name="location_on" size={14} color={C.accent} />
                <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{branchName}</span>
                <Icon name="expand_more" size={14} color={C.t3} style={{ transform: open ? 'rotate(180deg)' : 'none', marginLeft: 2 }} />
            </div>

            {open && dropdownRect && (
                <div
                    style={{
                        position: 'fixed',
                        top: dropdownRect.top,
                        left: dropdownRect.left,
                        width: dropdownRect.width,
                        background: C.surface,
                        border: `1px solid ${C.divider}`,
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        zIndex: 10001,
                        maxHeight: 220,
                        overflowY: 'auto',
                    }}
                >
                    {loading && (
                        <div style={{ padding: 12, fontSize: 11, color: C.t3 }}>Loading branches…</div>
                    )}
                    {error && (
                        <div style={{ padding: 12, fontSize: 11, color: C.red }}>{error}</div>
                    )}
                    {!loading && !error && locations.length === 0 && (
                        <div style={{ padding: 12, fontSize: 11, color: C.t3 }}>No branches found</div>
                    )}
                    {!loading && locations.map((loc) => {
                        const id = loc.id ?? loc.locationId;
                        const fullName = loc.locationName ?? loc.name ?? 'Branch';
                        const name = fullName.includes(',') ? fullName.split(',').pop().trim() : fullName;
                        const isSelected = branch?.id === id || branch?.locationId === id;
                        return (
                            <div
                                key={id || name}
                                role="button"
                                tabIndex={0}
                                onClick={() => selectBranch(loc)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') selectBranch(loc); }}
                                style={{
                                    padding: '10px 12px',
                                    fontSize: 12,
                                    fontWeight: isSelected ? 600 : 500,
                                    color: isSelected ? C.accent : C.t1,
                                    background: isSelected ? `${C.accent}10` : 'transparent',
                                    cursor: 'pointer',
                                    borderBottom: `1px solid ${C.divider}`,
                                }}
                            >
                                {name}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default OutletDropdown;

